import { formatNumber } from '@angular/common';
import { TYPE } from '@com-delta-idp/mnx-skymiles-overview';
import {
  AEMErrorMessage,
  LevelType,
  UserSession,
  getEmptyContent,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { logError } from '@com-delta-idp/mnx-utils';
import { MedallionBanner } from '@com-delta-omnichannel/aura';
import { AEMMedallionBannerContent, MedallionBannerDetails } from '../models';
import {
  getMedallionBannerAriaLabel,
  getMedallionItemAction,
  getName,
  getSalutationMessage,
  getSkyPriority,
  getTierName,
  getTierSubtitle
} from '../utils/medallion-banner.utils';

export const getMedallionBanner = (
  aemMedallionBanner: AEMMedallionBannerContent | undefined,
  profile: UserSession | undefined,
  featureFlag: boolean | undefined,
  error: string | undefined,
  aemErrorMessages: AEMErrorMessage[] | undefined
): MedallionBanner => {
  try {
    if (error) {
      return getEmptyContent<MedallionBanner>(error, aemErrorMessages);
    }
    return isValidData<
      AEMMedallionBannerContent,
      [
        AEMMedallionBannerContent | undefined,
        UserSession | undefined,
        boolean | undefined
      ]
    >('medallionBanner', aemMedallionBanner, profile, featureFlag)
      ? adaptMedallionBannerObject(
          (<AEMMedallionBannerContent>aemMedallionBanner).medallionBanner,
          profile as UserSession,
          featureFlag as boolean
        )
      : getEmptyContent<MedallionBanner>();
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'LANDING_PAGE', 'MEDALLION_BANNER');
    return getEmptyContent<MedallionBanner>(
      'full-page',
      aemErrorMessages,
      true,
      true
    );
  }
};

const adaptMedallionBannerObject = (
  medallionBannerDetails: MedallionBannerDetails,
  profile: UserSession,
  featureFlag: boolean
): MedallionBanner => {
  const index = medallionBannerDetails.medallionDetails.medallion.findIndex(
    (item) => item.medallionId == profile.membershipLevel
  );
  let medallionMemberLevel = profile.millionMilerTier
    ? parseInt(profile.millionMilerTier, 10).toString()
    : '0';
  medallionMemberLevel =
    profile.millionMilerTier && parseInt(profile.millionMilerTier, 10) > 20
      ? '0'
      : medallionMemberLevel;
  const enrolledYear: string = profile.enrollmentDate.split('-')[0];
  return {
    details: {
      title: `${getSalutationMessage(medallionBannerDetails)}, ${getName(
        profile
      )}`,
      tier: {
        title: `${medallionBannerDetails.statusLabel.replace(
          TYPE.YEAR_PLACEHOLDER,
          new Date().getFullYear().toString()
        )} `,
        subtitle: getTierSubtitle(medallionBannerDetails, profile, index),
        level: medallionMemberLevel as LevelType,
        name: getTierName(medallionBannerDetails, profile, index)
      },
      action: getMedallionItemAction(
        medallionBannerDetails.medallionDetails.medallion[index],
        profile.is360Tier
      ),
      milesData: {
        skyMiles: {
          title: medallionBannerDetails.accountNumberLabel,
          subtitle: profile.smNumber
        },
        miles: {
          title: medallionBannerDetails.availableMilesLabel,
          subtitle: formatNumber(
            Number(profile.smBalance),
            TYPE.LOCALE,
            TYPE.DIGITS_INFO
          ).toString()
        }
      }
    },
    status: {
      priority: getSkyPriority(medallionBannerDetails, profile),
      tier: {
        title: `${medallionBannerDetails.statusLabel.replace(
          TYPE.YEAR_PLACEHOLDER,
          new Date().getFullYear().toString()
        )} `,
        subtitle: getTierSubtitle(medallionBannerDetails, profile, index),
        level: medallionMemberLevel as LevelType,
        name: getTierName(medallionBannerDetails, profile, index)
      },
      enrollDate: `${medallionBannerDetails.showDurationLabel.toUpperCase()} ${enrolledYear}`
    },
    ariaLabel: getMedallionBannerAriaLabel(
      medallionBannerDetails.ariaLabel,
      medallionMemberLevel
    ),
    disabled: !featureFlag,
    loaded: true,
    analyticsEvent: {
      name: medallionBannerDetails.analyticsEvent.name,
      value: medallionBannerDetails.analyticsEvent.value
    }
  };
};
